// Home: every project the signed-in person can see, sites on top and apps below, each in one card
// with a search box, a List/Grid switch and (for admins) "+ Add site" / "+ Add app". Clicking a row
// opens its Overview; an app's error count opens its Errors. Sorting and searching are client-side,
// per section; the order and the List/Grid choice are remembered in localStorage.
import type { Home, HomeApp, HomeSite, HomeTotals } from "../src/home-types";
import { deltaPill, sparkline } from "./chart";
import { renderInstall } from "./install";
import { fmtUptime, uptimeTone } from "./uptime";

type Kind = "site" | "app";
type View = "list" | "grid";
type Project = HomeSite | HomeApp;

interface Opts {
  el: HTMLElement;
  range: string;
  mobile: boolean;
  current: () => boolean;
  errorCard: (err: unknown) => string;
  /** Super-admins can add sites and apps. */
  admin?: boolean;
  /** Called after a project is added, so the app can reload its project list. */
  onCreated?: () => Promise<void> | void;
}

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
const fmtN = (n: number) => (n >= 10_000 ? `${(n / 1000).toFixed(1)}k` : Math.round(n).toLocaleString("en-US"));
/** Error rate as a percentage: one decimal under 10% so small rates don't round to 0 or 1. */
const fmtRate = (x: number | null) => {
  if (x === null) return "–";
  const p = x * 100;
  if (!p) return "0%";
  if (p < 0.1) return "<0.1%";
  return p < 10 ? `${p.toFixed(1)}%` : `${Math.round(p)}%`;
};
const fmtDur = (ms: number | null) => {
  if (ms === null) return "–";
  const s = Math.round(ms / 1000);
  if (s >= 3600) return `${Math.floor(s / 3600)}h ${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}m`;
  return s >= 60 ? `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s` : `${s}s`;
};
const perSession = (a: HomeApp) => (a.sessions ? a.screens / a.sessions : null);
const fmtPer = (x: number | null) => (x === null ? "–" : x.toFixed(1));
const RANGE_LABEL: Record<number, string> = { 1: "Today", 7: "Last 7 days", 30: "Last 30 days", 90: "Last 90 days" };

/** People this period vs the one before: null when there's nothing to compare against. */
function change(s: Project): number | null {
  return s.previous_visitors ? (s.visitors - s.previous_visitors) / s.previous_visitors : null;
}

/** The change as a tiny pill under the headline number; nothing when there's nothing to compare. */
function delta(s: Project, days: number): string {
  const c = change(s);
  if (c === null) return "";
  const r = Math.round(c * 100);
  const cls = r > 0 ? "good" : r < 0 ? "bad" : "none";
  return `<span class="hl-delta ${cls}" title="Against the ${days === 1 ? "day" : `${days} days`} before">${r > 0 ? "+" : ""}${r}%</span>`;
}

/** 30-day uptime in green, amber or red; "Down" while it is; a dash for sites not checked yet. */
function uptimeCell(s: HomeSite): string {
  const u = s.uptime;
  if (!u) return `<span class="muted">–</span>`;
  const label = u.status === "down" ? "Down now" : u.status === "up" ? "Up" : "Not checked recently";
  return `<span class="hl-up ${u.status === "down" ? "bad" : uptimeTone(u.uptime_30d)}" title="${label}, ${fmtUptime(u.uptime_30d)} over 30 days">${u.status === "down" ? "Down" : fmtUptime(u.uptime_30d)}</span>`;
}

/** An app's errors in the period; when there are any, a link to its Errors screen. */
function errorsCell(a: HomeApp, label = ""): string {
  if (!a.errors) return `<span class="muted">0${label}</span>`;
  // A span, not a link: the row is already a button. app.ts opens /project/<id>/errors/ on click.
  return `<span class="home-err" data-home-errors="${esc(a.id)}" title="Open Errors for ${esc(a.name)}"><b class="num">${fmtN(a.errors)}</b>${label}</span>`;
}

/** A site's error rate: a dash without visits, a muted 0%, otherwise in red. */
function errorRateCell(s: HomeSite): string {
  if (s.error_rate === null) return `<span class="muted">–</span>`;
  if (!s.errored_sessions) return `<span class="muted">0%</span>`;
  const title = `${fmtN(s.errored_sessions)} of ${fmtN(s.sessions)} ${s.sessions === 1 ? "visit" : "visits"} had an error`;
  return `<span class="hl-err" title="${title}"><b class="num">${fmtRate(s.error_rate)}</b></span>`;
}

/** Who is on now: a green "● N online" pill, or a grey dot and a muted 0. */
const live = (s: Project, word: string) =>
  s.live
    ? `<span class="hl-live"><span class="dot"><i></i><i></i></span>${fmtN(s.live)} ${word}</span>`
    : `<span class="hl-off" title="Nobody ${word} now"><i></i>0</span>`;

/** Soft tints for the letter badges, picked by a hash of the id so a site keeps its colour. */
const TINTS = ["blue", "sand", "green", "lilac", "rose", "teal", "amber", "slate"];
function tint(id: string): string {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) h = Math.imul(h ^ id.charCodeAt(i), 16777619);
  return TINTS[(h >>> 0) % TINTS.length]!;
}
const nameCell = (s: Project) =>
  `<span class="home-site"><span class="hl-badge t-${tint(s.id)}" aria-hidden="true">${esc((s.name.replace(/[^a-z0-9]/gi, "")[0] ?? "?").toUpperCase())}</span><span class="home-name"><b>${esc(s.name)}</b><span>${esc(s.org)}</span></span></span>`;

/** One table column: header, cell, sort value, and whether a first click sorts low to high. */
interface Col<T> {
  key: string;
  label: string;
  title?: string;
  cell: (s: T, days: number) => string;
  value?: (s: T) => number | string;
  asc?: boolean;
  /** The headline number, larger and in ink. */
  primary?: boolean;
}

const nameCol = { key: "name", label: "", cell: nameCell, value: (s: Project) => s.name.toLowerCase(), asc: true };
const sparkCol = { key: "", label: "", cell: (s: Project) => `<span class="hl-spark">${spark(s.daily)}</span>` };
const headline = (s: Project, days: number) => `<b class="num">${fmtN(s.visitors)}</b>${delta(s, days)}`;

const SITE_COLS: Col<HomeSite>[] = [
  { ...nameCol, label: "Site" },
  sparkCol,
  { key: "visitors", label: "Visitors", cell: headline, value: (s) => s.visitors, primary: true },
  { key: "sessions", label: "Visits", cell: (s) => fmtN(s.sessions), value: (s) => s.sessions },
  { key: "pageviews", label: "Views", cell: (s) => fmtN(s.pageviews), value: (s) => s.pageviews },
  // Worst first on the first click; sites without visits sort below a clean 0%.
  { key: "error_rate", label: "Error rate", title: "Share of visits with at least one error", cell: (s) => errorRateCell(s), value: (s) => s.error_rate ?? -1 },
  { key: "live", label: "Online", cell: (s) => live(s, "online"), value: (s) => s.live },
  // Down sites first when sorting low to high, then by 30-day uptime; unchecked sites last.
  { key: "uptime", label: "Uptime", cell: uptimeCell, value: (s) => (s.uptime ? (s.uptime.status === "down" ? -1 : (s.uptime.uptime_30d ?? 2)) : 3), asc: true },
];

const APP_COLS: Col<HomeApp>[] = [
  { ...nameCol, label: "App" },
  sparkCol,
  { key: "visitors", label: "Users", title: "Active users", cell: headline, value: (s) => s.visitors, primary: true },
  { key: "sessions", label: "Sessions", cell: (s) => fmtN(s.sessions), value: (s) => s.sessions },
  { key: "per_session", label: "Screens/session", title: "Screen views per session", cell: (s) => fmtPer(perSession(s)), value: (s) => perSession(s) ?? -1 },
  { key: "avg_session", label: "Avg session", title: "Average time from first to last event in a session", cell: (s) => fmtDur(s.avg_session_ms), value: (s) => s.avg_session_ms ?? -1 },
  { key: "errors", label: "Errors", cell: (s) => errorsCell(s), value: (s) => s.errors },
  { key: "live", label: "Active", title: "Active in the last 5 minutes", cell: (s) => live(s, "active"), value: (s) => s.live },
];

// The sort order per section, remembered across reloads (localStorage; in memory if blocked).
const SORT_KEY = (k: Kind) => `monolog:home-sort:${k}`;
function loadSort(k: Kind): { key: string; desc: boolean } {
  try {
    const v = JSON.parse(localStorage.getItem(SORT_KEY(k)) ?? "null") as { key?: unknown; desc?: unknown } | null;
    if (v && typeof v.key === "string" && typeof v.desc === "boolean") return { key: v.key, desc: v.desc };
  } catch {}
  return { key: "visitors", desc: true };
}
function saveSort(k: Kind): void {
  try {
    localStorage.setItem(SORT_KEY(k), JSON.stringify(sort[k]));
  } catch {}
}
const sort: Record<Kind, { key: string; desc: boolean }> = { site: loadSort("site"), app: loadSort("app") };
const query: Record<Kind, string> = { site: "", app: "" };
let last: { o: Opts; data: Home } | null = null;

// The List/Grid choice, kept in memory too so the switch works where storage is blocked.
const VIEW_KEY = (k: Kind) => `monolog:home-view:${k}`;
const views: Partial<Record<Kind, View>> = {};
function getView(k: Kind): View {
  if (views[k]) return views[k];
  try {
    return (views[k] = localStorage.getItem(VIEW_KEY(k)) === "grid" ? "grid" : "list");
  } catch {
    return (views[k] = "list");
  }
}
function setView(k: Kind, v: View): void {
  views[k] = v;
  try {
    localStorage.setItem(VIEW_KEY(k), v);
  } catch {}
}

function sorted<T extends Project>(rows: T[], cols: Col<T>[], kind: Kind): T[] {
  const { key, desc } = sort[kind];
  const value = cols.find((c) => c.key === key)?.value ?? ((s: T) => s.visitors);
  return [...rows].sort((a, b) => {
    const x = value(a);
    const y = value(b);
    const c = x < y ? -1 : x > y ? 1 : a.name.localeCompare(b.name);
    return desc ? -c : c;
  });
}

/** Rows whose name, organisation or domain contain what's typed in the section's search box. */
function filtered<T extends Project>(rows: T[], kind: Kind): T[] {
  const q = query[kind].trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((r) => [r.name, r.org, r.id, "domain" in r ? r.domain ?? "" : ""].some((v) => v.toLowerCase().includes(q)));
}

/**
 * People per day: a blue line over a light blue fill, with a dot on the last day. The dot is a
 * zero-length round-capped stroke, so it stays round however the SVG is stretched. Flat and faint
 * when there's no data.
 */
function spark(values: number[]): string {
  const n = values.length;
  if (n < 2) return "";
  const mx = Math.max(...values);
  const y = (v: number) => (mx ? 36 - (v / mx) * 30 : 36).toFixed(1);
  const pts = values.map((v, i) => [((i / (n - 1)) * 100).toFixed(1), y(v)] as const);
  const line = `M${pts.map((p) => p.join(" ")).join(" L")}`;
  const [lx, ly] = pts[n - 1]!;
  const op = mx ? 1 : 0.35;
  return `<svg viewBox="0 0 100 40" preserveAspectRatio="none" aria-hidden="true" style="opacity:${op}"><path d="${line} L100 40 L0 40 Z" fill="var(--blue-tint)"/><path d="${line}" fill="none" stroke="var(--blue)" stroke-width="1.75" vector-effect="non-scaling-stroke" stroke-linejoin="round" stroke-linecap="round"/><path d="M${lx} ${ly} Z" stroke="var(--card)" stroke-width="9" stroke-linecap="round" vector-effect="non-scaling-stroke"/><path d="M${lx} ${ly} Z" stroke="var(--blue)" stroke-width="6" stroke-linecap="round" vector-effect="non-scaling-stroke"/></svg>`;
}

export async function renderHome(o: Opts): Promise<void> {
  o.el.innerHTML = `<div class="home"><div class="card"><div class="skeleton" style="height:320px"></div></div></div>`;
  let data: Home;
  try {
    data = await load(o.range);
  } catch (err) {
    if (o.current()) o.el.innerHTML = o.errorCard(err);
    return;
  }
  if (!o.current()) return;
  last = { o, data };
  draw(o, data);
}

async function load(range: string): Promise<Home> {
  const res = await fetch(`/api/home?range=${encodeURIComponent(range)}`, { credentials: "same-origin" });
  const body = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
  if (!res.ok) throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`);
  const data = body as Home;
  data.apps ??= [];
  return data;
}

/** The Overview metric strip, for every site added up. Read-only here: no metric to switch to. */
const STRIP: { label: string; get: (t: HomeTotals) => number | null; fmt: (v: number | null) => string; invert?: boolean }[] = [
  { label: "Visitors", get: (t) => t.visitors, fmt: (v) => fmtN(v ?? 0) },
  { label: "Visits", get: (t) => t.sessions, fmt: (v) => fmtN(v ?? 0) },
  { label: "Pageviews", get: (t) => t.pageviews, fmt: (v) => fmtN(v ?? 0) },
  { label: "Error rate", get: (t) => t.error_rate, fmt: fmtRate, invert: true },
  { label: "Visit length", get: (t) => t.avg_duration_ms, fmt: fmtDur },
];

function strip(o: Opts, h: Home): string {
  const { current, previous, daily } = h.site_totals;
  const cells = STRIP.map((m) => {
    const cur = m.get(current);
    const pill = deltaPill(cur, m.get(previous), m.invert);
    if (o.mobile) return `<div class="m-kpi"><span class="label">${m.label}</span><span class="vr"><span class="v num">${m.fmt(cur)}</span>${pill}</span></div>`;
    const series = daily.map(m.get);
    return `<div class="kpi"><span class="label">${m.label}</span><span class="value num">${m.fmt(cur)}</span>
      <span class="foot">${pill}${series.some((v) => v !== null) ? sparkline(series, "#A9B0BF") : ""}</span></div>`;
  }).join("");
  return o.mobile ? `<div class="m-kpis home-kpis" aria-label="All sites">${cells}</div>` : `<section class="card flush" aria-label="All sites"><div class="kpis home-kpis">${cells}</div></section>`;
}

/** "N visitors online now" across every site: in the toolbar on desktop, above the strip on mobile. */
function livePill(o: Opts, h: Home): string {
  const n = h.site_totals.live;
  const text = `${fmtN(n)} ${n === 1 ? "visitor" : "visitors"} online now`;
  const desk = document.getElementById("home-live");
  if (desk) {
    desk.hidden = o.mobile || !h.sites.length;
    const t = desk.querySelector(".home-live-text");
    if (t) t.textContent = text;
  }
  return o.mobile && h.sites.length ? `<div class="m-row"><span class="m-live"><span class="dot"><i></i><i></i></span>${text}</span></div>` : "";
}

function table<T extends Project>(kind: Kind, rows: T[], cols: Col<T>[], days: number): string {
  const s = sort[kind];
  const head = (c: Col<T>, i: number) =>
    c.key
      ? `<button class="home-sort${i ? " r" : ""}" data-home-sort="${kind}:${c.key}"${c.title ? ` title="${c.title}"` : ""} aria-sort="${s.key === c.key ? (s.desc ? "descending" : "ascending") : "none"}">${c.label}${s.key === c.key ? `<span aria-hidden="true">${s.desc ? "↓" : "↑"}</span>` : ""}</button>`
      : `<span class="home-sort hl-range">${RANGE_LABEL[days] ?? `Last ${days} days`}</span>`;
  const cell = (c: Col<T>, i: number, r: T) => (i === 0 || !c.key ? c.cell(r, days) : `<span class="n${c.primary ? " p" : ""}">${c.cell(r, days)}</span>`);
  return `<div class="table-scroll"><div class="hl-table">
    <div class="hl-row ${kind} hl-rh">${cols.map(head).join("")}</div>
    ${sorted(rows, cols, kind)
      .map((r) => `<button class="hl-row ${kind}${r.live ? " is-live" : ""}" data-home-site="${esc(r.id)}">${cols.map((c, i) => cell(c, i, r)).join("")}</button>`)
      .join("")}
  </div></div>`;
}

function siteCards(sites: HomeSite[], days: number): string {
  return `<div class="hl-cards">${sorted(sites, SITE_COLS, "site")
    .map(
      (s) => `<button class="hl-card${s.live ? " is-live" : ""}" data-home-site="${esc(s.id)}">
        <div class="home-card-top">${nameCell(s)}${live(s, "online")}</div>
        <div class="home-card-mid"><div class="hl-big">${headline(s, days)}<span>visitors</span></div><div class="hl-spark">${spark(s.daily)}</div></div>
        <div class="home-card-low"><span><b class="num">${fmtN(s.sessions)}</b> visits</span><span><b class="num">${fmtN(s.pageviews)}</b> views</span><span>${errorRateCell(s)} error rate</span>${s.uptime ? `<span class="home-card-up">${uptimeCell(s)} <span class="muted">uptime</span></span>` : ""}</div>
      </button>`,
    )
    .join("")}</div>`;
}

function appCards(apps: HomeApp[], days: number): string {
  return `<div class="hl-cards">${sorted(apps, APP_COLS, "app")
    .map(
      (a) => `<button class="hl-card${a.live ? " is-live" : ""}" data-home-site="${esc(a.id)}">
        <div class="home-card-top">${nameCell(a)}${live(a, "active")}</div>
        <div class="home-card-mid"><div class="hl-big">${headline(a, days)}<span>active users</span></div><div class="hl-spark">${spark(a.daily)}</div></div>
        <div class="home-card-low"><span><b class="num">${fmtN(a.sessions)}</b> sessions</span><span><b class="num">${fmtPer(perSession(a))}</b> screens/session</span><span><b class="num">${fmtDur(a.avg_session_ms)}</b> avg</span><span class="home-card-up">${errorsCell(a, a.errors === 1 ? " error" : " errors")}</span></div>
      </button>`,
    )
    .join("")}</div>`;
}

const NOUN: Record<Kind, [string, string, string]> = { site: ["site", "sites", "with visits"], app: ["app", "apps", "with users"] };

function countText(kind: Kind, rows: Project[]): string {
  const [, many, what] = NOUN[kind];
  return `${rows.filter((r) => r.visitors > 0).length} of ${rows.length} ${rows.length === 1 ? NOUN[kind][0] : many} ${what}`;
}

/** The section's rows (or cards) for what's typed in its search box, and the count beside it. */
function body(o: Opts, h: Home, kind: Kind): { html: string; count: string } {
  const all: Project[] = kind === "site" ? h.sites : h.apps;
  const rows = filtered(all, kind);
  const days = h.range.days;
  const view = o.mobile ? "grid" : getView(kind);
  const html = !rows.length
    ? `<div class="hl-empty">No ${NOUN[kind][1]} match “${esc(query[kind].trim())}”.</div>`
    : view === "grid"
      ? kind === "site" ? siteCards(rows as HomeSite[], days) : appCards(rows as HomeApp[], days)
      : kind === "site" ? table("site", rows as HomeSite[], SITE_COLS, days) : table("app", rows as HomeApp[], APP_COLS, days);
  return { html, count: countText(kind, rows) };
}

const SEARCH_ICON = `<svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="7" cy="7" r="4.75" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M10.5 10.5 14 14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>`;

function section(o: Opts, h: Home, kind: Kind, title: string): string {
  const [one, many] = NOUN[kind];
  const view = getView(kind);
  const b = body(o, h, kind);
  return `<section class="card flush home-list" aria-labelledby="home-${kind}">
    <h2 class="sr-only" id="home-${kind}">${title}</h2>
    <div class="hl-head">
      <label class="hl-search">${SEARCH_ICON}<input type="search" class="in" data-home-q="${kind}" placeholder="Search ${many}" aria-label="Search ${many}" value="${esc(query[kind])}" autocomplete="off" spellcheck="false"></label>
      <span class="hl-count" data-home-count="${kind}" aria-live="polite">${b.count}</span>
      <span class="spacer"></span>
      <div class="seg hl-view" role="group" aria-label="Layout">${(["list", "grid"] as const)
        .map((v) => `<button data-home-view="${kind}:${v}" aria-pressed="${view === v}">${v === "list" ? "List" : "Grid"}</button>`)
        .join("")}</div>
      ${o.admin ? `<button class="btn ink hl-add" data-home-add="${kind}"><span aria-hidden="true">+</span> Add ${one}</button>` : ""}
    </div>
    <div class="hl-body" data-home-body="${kind}">${b.html}</div>
  </section>`;
}

function draw(o: Opts, h: Home): void {
  if (!h.sites.length && !h.apps.length) {
    o.el.innerHTML = `<div class="home"><h1 class="home-h">Home</h1><div class="notice">You don't have access to any sites or apps yet.</div>${
      o.admin ? `<div class="btns"><button class="btn ink hl-add" data-home-add="site">+ Add site</button><button class="btn hl-add" data-home-add="app">+ Add app</button></div>` : ""
    }</div>`;
    return;
  }
  const note = h.available ? "" : `<div class="notice">The live event store isn't connected, so the numbers below are empty.</div>`;
  const down = h.sites.filter((s) => s.uptime?.status === "down");
  const downNote = down.length
    ? `<div class="notice error"><b>${down.length === 1 ? "1 site is" : `${down.length} sites are`} down right now:</b> ${down.map((s) => esc(s.domain ?? s.name)).join(", ")}</div>`
    : "";
  // An admin with no apps yet still gets the Apps card, so there's somewhere to add the first one.
  const sites = h.sites.length || o.admin ? section(o, h, "site", "Sites") : "";
  const apps = h.apps.length || o.admin ? section(o, h, "app", "Apps") : "";
  const ranges = o.mobile
    ? `<div class="mseg" role="group" aria-label="Date range">${[["today", "Today"], ["7d", "7d"], ["30d", "30d"], ["90d", "90d"]]
        .map(([v, l]) => `<button data-range="${v}" aria-pressed="${v === o.range}">${l}</button>`)
        .join("")}</div>`
    : "";
  const top = h.sites.length ? strip(o, h) : "";
  o.el.innerHTML = `<div class="home">${livePill(o, h)}${ranges}<h1 class="sr-only">Home</h1>${note}${downNote}${top}${sites}${apps}</div>`;
}

/** Redraw one section's rows and count, leaving its search box (and the caret in it) alone. */
function redrawBody(kind: Kind): void {
  if (!last || !last.o.current()) return;
  const b = body(last.o, last.data, kind);
  const el = last.o.el.querySelector<HTMLElement>(`[data-home-body="${kind}"]`);
  const count = last.o.el.querySelector<HTMLElement>(`[data-home-count="${kind}"]`);
  if (el) el.innerHTML = b.html;
  if (count) count.textContent = b.count;
}

document.addEventListener("click", (ev) => {
  const t = ev.target as HTMLElement;
  const b = t.closest<HTMLElement>("[data-home-sort]");
  if (b && last) {
    const [kind, k] = b.dataset.homeSort!.split(":") as [Kind, string];
    const col = (kind === "site" ? SITE_COLS : APP_COLS).find((c) => c.key === k);
    if (!col) return;
    if (sort[kind].key === k) sort[kind].desc = !sort[kind].desc;
    else sort[kind] = { key: k, desc: !col.asc };
    saveSort(kind);
    return redrawBody(kind);
  }
  const v = t.closest<HTMLElement>("[data-home-view]");
  if (v && last) {
    const [kind, view] = v.dataset.homeView!.split(":") as [Kind, View];
    setView(kind, view);
    for (const x of v.parentElement!.querySelectorAll<HTMLElement>("[data-home-view]")) x.setAttribute("aria-pressed", String(x === v));
    return redrawBody(kind);
  }
  const add = t.closest<HTMLElement>("[data-home-add]");
  if (add && last) return openAdd(add.dataset.homeAdd as Kind);
  if (t.closest("[data-add-close]") || t.id === "add-panel") return closeAdd();
});

document.addEventListener("input", (ev) => {
  const t = ev.target as HTMLElement;
  if (!(t instanceof HTMLInputElement) || !t.dataset.homeQ) return;
  const kind = t.dataset.homeQ as Kind;
  query[kind] = t.value;
  redrawBody(kind);
});

document.addEventListener("keydown", (ev) => {
  if (ev.key === "Escape" && !document.getElementById("add-panel")?.hidden) closeAdd();
});

// ---------- "+ Add site" / "+ Add app" (admins; POST /api/projects, src/projects.ts) ----------

interface Created {
  id: string;
  name: string;
  kind: Kind;
  org: string;
  write_key: string;
  allowed_domains: string[];
}

const ADD_ERRORS: Record<string, string> = {
  bad_name: "Give it a name (up to 80 characters).",
  bad_domain: "Enter a domain like example.com.",
  domain_taken: "Another site already takes events from this domain.",
  bad_org: "Pick an organisation or type a new one.",
  unknown_org: "That organisation no longer exists.",
  bad_timezone: "Pick a timezone from the list.",
  not_allowed: "Only MonoBlock admins can add sites and apps.",
};

let addKind: Kind = "site";
let addReturn: HTMLElement | null = null;

function addPanel(): HTMLElement {
  let el = document.getElementById("add-panel");
  if (!el) {
    el = document.createElement("div");
    el.id = "add-panel";
    el.className = "share-back";
    el.hidden = true;
    document.body.append(el);
  }
  return el;
}

function zones(): string[] {
  const all = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.("timeZone") ?? [];
  return all.includes("Asia/Dubai") ? all : ["Asia/Dubai", ...all];
}

/** Organisation names for the picker: everyone's orgs for an admin (from /api/team), else what Home shows. */
async function orgNames(): Promise<string[]> {
  const seen = new Set([...(last?.data.sites ?? []), ...(last?.data.apps ?? [])].map((p) => p.org));
  try {
    const res = await fetch("/api/team", { credentials: "same-origin" });
    if (res.ok) for (const o of ((await res.json()) as { orgs: { name: string }[] }).orgs) seen.add(o.name);
  } catch {}
  return [...seen].sort((a, b) => a.localeCompare(b));
}

function openAdd(kind: Kind): void {
  addKind = kind;
  addReturn = document.activeElement as HTMLElement | null;
  const el = addPanel();
  const word = kind === "site" ? "site" : "app";
  el.innerHTML = `<section class="card share-card add-card" role="dialog" aria-modal="true" aria-labelledby="add-h">
    <div class="card-head"><h2 id="add-h">Add ${word}</h2><span class="spacer"></span><button class="link" type="button" data-add-close aria-label="Close">Close</button></div>
    <form class="add-form" data-add-form novalidate>
      <label class="fld"><span>Name</span><input class="in" name="name" maxlength="80" required placeholder="${kind === "site" ? "e.g. Launchwise" : "e.g. Launchwise iOS"}" autocomplete="off"></label>
      ${kind === "site" ? `<label class="fld"><span>Domain</span><input class="in" name="domain" required placeholder="example.com" inputmode="url" autocapitalize="off" spellcheck="false" autocomplete="off"><span class="fld-help">Events are accepted from this domain and its www. version.</span></label>` : ""}
      <label class="fld"><span>Organisation</span><input class="in" name="org" list="add-orgs" required placeholder="Pick one or type a new name" autocomplete="off"><datalist id="add-orgs"></datalist></label>
      <label class="fld"><span>Timezone</span><select class="in" name="timezone">${zones()
        .map((z) => `<option${z === "Asia/Dubai" ? " selected" : ""}>${esc(z)}</option>`)
        .join("")}</select><span class="fld-help">Sets where a day starts for this ${word}'s reports.</span></label>
      <div class="add-err" aria-live="polite"></div>
      <div class="btns add-btns"><button class="btn" type="button" data-add-close>Cancel</button><button class="btn ink" type="submit">Add ${word}</button></div>
    </form>
  </section>`;
  el.hidden = false;
  el.querySelector<HTMLInputElement>("input[name=name]")?.focus();
  void orgNames().then((names) => {
    const dl = el.querySelector("#add-orgs");
    if (dl) dl.innerHTML = names.map((n) => `<option value="${esc(n)}"></option>`).join("");
  });
  el.querySelector<HTMLFormElement>("[data-add-form]")!.addEventListener("submit", (ev) => {
    ev.preventDefault();
    void submitAdd(ev.target as HTMLFormElement);
  });
}

function closeAdd(): void {
  const el = document.getElementById("add-panel");
  if (!el || el.hidden) return;
  el.hidden = true;
  el.innerHTML = "";
  addReturn?.focus?.();
}

async function submitAdd(form: HTMLFormElement): Promise<void> {
  const f = new FormData(form);
  const btn = form.querySelector<HTMLButtonElement>("button[type=submit]")!;
  const errBox = form.querySelector<HTMLElement>(".add-err")!;
  const str = (k: string) => String(f.get(k) ?? "").trim();
  btn.disabled = true;
  errBox.innerHTML = "";
  let created: Created;
  try {
    const res = await fetch("/api/projects", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ kind: addKind, name: str("name"), ...(addKind === "site" ? { domain: str("domain") } : {}), org: str("org"), timezone: str("timezone") }),
    });
    const out = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
    if (!res.ok) throw new Error((out as { error?: string }).error ?? `HTTP ${res.status}`);
    created = out as Created;
  } catch (e) {
    const code = (e as Error).message;
    errBox.innerHTML = `<div class="form-err">${esc(ADD_ERRORS[code] ?? `Couldn't add it (${code}).`)}</div>`;
    btn.disabled = false;
    return;
  }
  showCreated(created);
  // The app's project list first (so the new row opens), then Home's numbers.
  const o = last?.o;
  if (!o) return;
  try {
    await o.onCreated?.();
    const data = await load(o.range);
    if (!o.current()) return;
    last = { o, data };
    draw(o, data);
  } catch {}
}

function showCreated(c: Created): void {
  const el = addPanel();
  const card = el.querySelector<HTMLElement>(".add-card");
  if (!card) return;
  const word = c.kind === "site" ? "site" : "app";
  card.innerHTML = `<div class="card-head"><h2 id="add-h">${esc(c.name)} added</h2><span class="spacer"></span><button class="link" type="button" data-add-close aria-label="Close">Close</button></div>
    <div class="share-new"><span>${c.kind === "site" ? `Add the snippet below to every page of <b>${esc(c.allowed_domains[0] ?? "")}</b>. Visits show up on Home within a minute of the first one.` : `Give the app's SDK this write key. Sessions show up on Home within a minute of the first one.`}</span></div>
    <div class="add-install"></div>
    <div class="btns add-btns"><button class="btn" type="button" data-home-site="${esc(c.id)}" data-add-close>Open ${word}</button><button class="btn ink" type="button" data-add-close>Done</button></div>`;
  const box = card.querySelector<HTMLElement>(".add-install")!;
  if (c.kind === "site") void renderInstall(box, { id: c.id, name: c.name });
  else
    box.innerHTML = `<div class="inst-card"><section><h3>Write key</h3><div class="inst-code"><pre id="add-key"><code>${esc(c.write_key)}</code></pre><button class="btn" type="button" data-copy-key>Copy</button></div><p class="muted">Public: it identifies the app, it isn't a secret. Project id <code>${esc(c.id)}</code>.</p></section></div>`;
  box.querySelector<HTMLButtonElement>("[data-copy-key]")?.addEventListener("click", (e) => {
    const b = e.currentTarget as HTMLButtonElement;
    void navigator.clipboard?.writeText(c.write_key).then(() => (b.textContent = "Copied"));
  });
  card.querySelector<HTMLElement>("[data-add-close]")?.focus();
}
